import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import './QRScanner.css';

function QRScanner() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(null);
  const [scannedResult, setScannedResult] = useState(null);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    startScanning();
    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      setScanning(true);
    } catch (err) {
      setError('Unable to access camera: ' + err.message);
    }
  };

  const stopScanning = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  // The <video> element only exists once scanning is true, so attach the stream and
  // start the scan loop after that render. The loop is cancelled when scanning stops.
  useEffect(() => {
    if (!scanning || !videoRef.current || !streamRef.current) return undefined;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    video.srcObject = streamRef.current;

    const tick = () => {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        const ctx = canvas.getContext('2d');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);

        if (code) {
          setScannedResult(code.data);
          stopScanning();
          handleScannedCode(code.data);
          return;
        }
      }
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [scanning]);

  // The code in a QR label is the record's route (/items/<code> or /locations/<code>),
  // which is a different identifier from the record's id, so look it up first.
  const handleScannedCode = async (qrCode) => {
    try {
      const match = qrCode.match(/\/(items|locations)\/([0-9a-fA-F-]{36})/);
      if (!match) {
        setError('Invalid QR code format');
        return;
      }
      const [, kind, code] = match;
      const response = await fetch(`/api/${kind}/route/${code}`);
      if (!response.ok) {
        setError(response.status === 404
          ? 'This QR code does not match anything in StowIt (it may have been deleted)'
          : `Could not look up the QR code (${response.status})`);
        return;
      }
      const record = await response.json();
      // A scanned item opens the guide to where it belongs (an item with no location opens its page)
      navigate(kind === 'items' && record.storageLocationId ? `/guide/items/${record.id}` : `/${kind}/${record.id}`);
    } catch (err) {
      setError('Error processing QR code: ' + err.message);
    }
  };

  return (
    <div className="qr-scanner-page">
      <div className="scanner-container">
        <h1>Scan QR Code</h1>
        
        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')} className="close-btn">×</button>
          </div>
        )}

        {scanning ? (
          <>
            <div className="video-container">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="video-feed"
              ></video>
              <canvas
                ref={canvasRef}
                style={{ display: 'none' }}
              ></canvas>
              <div className="scanner-frame">
                <div className="corner corner-tl"></div>
                <div className="corner corner-tr"></div>
                <div className="corner corner-bl"></div>
                <div className="corner corner-br"></div>
              </div>
            </div>

            <p className="scanning-text">Position QR code within frame</p>

            <button onClick={stopScanning} className="btn btn-secondary">
              Stop Scanning
            </button>
          </>
        ) : (
          <>
            {scannedResult && (
              <div className="result-display">
                <p className="result-label">Scanned:</p>
                <p className="result-text">{scannedResult}</p>
              </div>
            )}
            <button onClick={startScanning} className="btn btn-primary">
              Start Scanning
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default QRScanner;
